import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useGamification } from '../context/GamificationContext';
import { useFavorites } from '../contexts/FavoritesContext';
import { useTheme } from '../contexts/ThemeContext';
import { ScoreCalculatorModal } from './ScoreCalculatorModal';
import { resetOnboarding } from './OnboardingGuide';
import { AnimatedLogo } from './AnimatedLogo';
import { ThemeSelector } from './ThemeSelector';
import { 
  LayoutDashboard, 
  Users, 
  Stethoscope, 
  Pill, 
  FileText, 
  Search, 
  Settings,
  LogOut,
  Menu,
  X,
  ScanEye,
  History,
  BookOpen,
  Calculator,
  ClipboardList,
  Star,
  ChevronDown,
  ChevronUp,
  ShieldCheck,
  HelpCircle,
  Library,
  Trophy
} from 'lucide-react';

export const Sidebar = ({ isOpen, setIsOpen }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { progress } = useGamification();
  const { favorites } = useFavorites();
  const { theme, c } = useTheme();
  const [favoritesExpanded, setFavoritesExpanded] = useState(true);
  const [modalScoreId, setModalScoreId] = useState(null);
  const [avatarError, setAvatarError] = useState(false);

  const menuItems = [
    { icon: LayoutDashboard, label: 'Accueil', path: '/dashboard', testId: 'nav-dashboard' },
    { icon: Search, label: 'Recherche Ciblée', path: '/medical-search', testId: 'nav-medical-search' },
    { icon: Stethoscope, label: 'Fiche Diagnostic', path: '/diagnosis', testId: 'nav-diagnosis' },
    { icon: ScanEye, label: 'Dermato', path: '/dermato', testId: 'nav-dermato' },
    { icon: ClipboardList, label: 'Scores Médicaux', path: '/scores', testId: 'nav-scores' },
    { icon: Pill, label: 'Médicaments - BDPM', path: '/medications', testId: 'nav-medications' },
    // Désactivé temporairement
    // { icon: ShieldCheck, label: 'Vérificateur Ordonnances', path: '/prescription', testId: 'nav-prescription' },
    { icon: Calculator, label: 'Cotation CCAM/NGAP', path: '/cotation', testId: 'nav-cotation' },
    { icon: BookOpen, label: 'Bibliographie', path: '/biblio', testId: 'nav-biblio' },
    { icon: History, label: 'Historique', path: '/history', testId: 'nav-history' },
    { icon: Trophy, label: 'Ma Progression', path: '/progress', testId: 'nav-progress' },
  ];

  if (user?.role === 'admin') {
    menuItems.push({ icon: Library, label: 'Ma Bibliothèque', path: '/library', testId: 'nav-library' });
    menuItems.push({ icon: Settings, label: 'Administration', path: '/admin', testId: 'nav-admin' });
  }

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <>
      {/* Mobile menu button */}
      <button
        data-testid="mobile-menu-button"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={isOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
        className={`lg:hidden fixed top-3 left-3 z-50 w-11 h-11 flex items-center justify-center rounded-xl backdrop-blur-xl border ${c('bgSecondary')} ${c('border')} ${c('textSecondary')} hover:${c('textPrimary')} active:scale-95 transition-transform`}
      >
        {isOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Overlay for mobile */}
      {isOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        data-testid="sidebar"
        className={`
          fixed top-0 left-0 h-[calc(100%-var(--regulatory-h))] w-full sm:w-72 lg:w-64 backdrop-blur-xl border-r z-50
          ${c('sidebarBg')} ${c('sidebarBorder')}
          transform transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0
        `}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className={`p-6 border-b ${c('border')}`}>
            <div className="flex items-center gap-3">
              <AnimatedLogo size={48} />
              <div className="flex-1">
                <h1 className={`text-xl font-bold ${c('logoText')}`}>Hippocrate MG</h1>
                <p className={`text-xs ${c('textMuted')}`}>Outil documentaire médical</p>
              </div>
              {/* Theme Selector */}
              <ThemeSelector />
            </div>
          </div>

          {/* User info */}
          <div className={`p-4 border-b ${c('border')}`}>
            <div className="flex items-center gap-3">
              {user?.picture && !avatarError ? (
                <img
                  src={user.picture}
                  alt={user?.full_name || 'Avatar'}
                  referrerPolicy="no-referrer"
                  onError={() => setAvatarError(true)}
                  className="w-10 h-10 rounded-full object-cover shrink-0 ring-2 ring-white/10"
                  data-testid="user-avatar-img"
                />
              ) : (
                <div data-testid="user-avatar-fallback" className={`w-10 h-10 rounded-full ${c('accent')} flex items-center justify-center text-white font-semibold shrink-0`}>
                  {user?.full_name?.charAt(0).toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${c('textPrimary')} truncate`}>{user?.full_name}</p>
                {progress?.grade ? (
                  <button
                    onClick={() => { navigate('/progress'); setIsOpen(false); }}
                    className="flex items-center gap-1.5 mt-0.5"
                    data-testid="sidebar-grade-chip"
                  >
                    <img src={progress.grade.badge} alt="" className="w-4 h-4 object-contain" />
                    <span className="text-[11px] text-amber-300/90 truncate">{progress.grade.nom}</span>
                  </button>
                ) : (
                  <p className={`text-xs ${c('textMuted')} capitalize`}>{user?.role}</p>
                )}
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto scrollbar-custom">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <button
                  key={item.path}
                  data-testid={item.testId}
                  onClick={() => {
                    navigate(item.path);
                    setIsOpen(false);
                  }}
                  className={`
                    w-full flex items-center gap-3.5 lg:gap-3 px-4 py-4 lg:py-3 rounded-2xl lg:rounded-xl font-medium transition-all active:scale-[0.98]
                    ${isActive 
                      ? `${c('accent')} ${c('textPrimary')} shadow-lg` 
                      : `${c('textMuted')} hover:${c('textPrimary')} ${c('bgHover')}`
                    }
                  `}
                >
                  <Icon size={22} className="lg:hidden shrink-0" />
                  <Icon size={20} className="hidden lg:block shrink-0" />
                  <span className="text-base lg:text-sm">{item.label}</span>
                </button>
              );
            })}

            {/* Favorites Section */}
            {favorites.length > 0 && (
              <div className={`mt-4 pt-4 border-t ${c('border')}`}>
                <button
                  onClick={() => setFavoritesExpanded(!favoritesExpanded)}
                  className={`w-full flex items-center justify-between px-4 py-2 ${c('textWarning')} transition-colors`}
                  data-testid="favorites-toggle"
                >
                  <div className="flex items-center gap-2">
                    <Star size={16} className="fill-current" />
                    <span className="text-sm font-medium">Scores Favoris</span>
                    <span className={`text-xs ${c('bgWarning')} px-1.5 py-0.5 rounded`}>{favorites.length}</span>
                  </div>
                  {favoritesExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
                
                {favoritesExpanded && (
                  <div className="mt-2 space-y-1">
                    {favorites.map((score) => (
                      <button
                        key={score.id}
                        onClick={() => setModalScoreId(score.id)}
                        className={`w-full flex items-center gap-2 px-4 py-2 ${c('textMuted')} hover:${c('textPrimary')} ${c('bgHover')} rounded-lg transition-colors text-left`}
                        data-testid={`favorite-${score.id}`}
                      >
                        <Calculator size={14} className={`${c('textWarning')} flex-shrink-0`} />
                        <span className="text-xs truncate">{score.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </nav>

          {/* Help & Logout */}
          <div className={`p-3 border-t ${c('border')} flex items-center gap-2`}>
            <button
              data-testid="show-guide-button"
              onClick={resetOnboarding}
              aria-label="Voir le guide"
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-3 lg:py-2.5 rounded-xl font-medium ${c('textMuted')} hover:${c('textPrimary')} ${c('bgHover')} transition-all active:scale-[0.98]`}
            >
              <HelpCircle size={18} className="shrink-0" />
              <span className="text-sm">Guide</span>
            </button>
            <button
              data-testid="logout-button"
              onClick={handleLogout}
              aria-label="Déconnexion"
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-3 lg:py-2.5 rounded-xl font-medium ${c('textDanger')} ${c('bgHover')} transition-all active:scale-[0.98]`}
            >
              <LogOut size={18} className="shrink-0" />
              <span className="text-sm">Déconnexion</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Score Calculator Modal */}
      <ScoreCalculatorModal
        isOpen={!!modalScoreId}
        onClose={() => setModalScoreId(null)}
        scoreId={modalScoreId}
      />
    </>
  );
};
